import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  Form,
  Alert,
  Table,
  ButtonGroup,
  Button,
  Badge,
  Placeholder,
} from "react-bootstrap";
import { FaChartLine, FaChartBar, FaTable, FaCalendarAlt, FaBullseye } from "react-icons/fa";
import Title from "../common/Title";
import ExportToExcelButton from "../common/ExportToExcelButton";
import { usageService } from "../../services/usage.service";
import { canAccessSuperUserAnalytics, isAdmin } from "../../utils/authUtils";
import {
  MONTH_RANGE_OPTIONS,
  formatUnits,
  formatUnitsExact,
  formatPercentOfLimit,
  formatChange,
  getConfidenceVariant,
  getTrendDisplay,
  monthOverMonthPeriod,
  pointLabel,
} from "../../utils/usageUtils";
import UsageInsights from "./UsageInsights";
import TierProgress from "./TierProgress";
import UsageTrendChart from "./UsageTrendChart";
import "../analytics/superuser/SuperUserAnalytics.css";

const FORECAST_MONTHS = 3;

/**
 * A single client's usage.
 *
 * Rendered both as the standalone "My Usage" page and, with an explicit clientId, as
 * the Product Team drill-down — mirroring how ClientAnalyticsView reuses the client
 * analytics view.
 */
function UsageAnalytics({ user, clientId, embedded = false }) {
  const [months, setMonths] = useState(12);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState("chart");

  // Only internal roles reach this page in this release. Product Team and SuperUser may
  // look at any client; an Admin only ever sees their own, which the API enforces
  // regardless of what the UI sends.
  const allowed = canAccessSuperUserAnalytics(user) || isAdmin(user) || Boolean(user?.isProductTeam);

  // Route prop first (the Product Team drill-down), then the client currently selected
  // in the portal. That selection lives in the user cookie, NOT in the JWT: a SuperUser
  // has no ClientId claim, so without this fallback the API has no client to scope to
  // and correctly refuses. Same resolution TrendsTab uses.
  const effectiveClientId = clientId || user?.clientId;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = { months, forecastMonths: FORECAST_MONTHS };
      // Omitted entirely rather than sent as undefined — the service strips nullish
      // values, but not sending it at all keeps the intent obvious.
      if (effectiveClientId) params.clientId = effectiveClientId;

      // Services return the raw axios response (there is no unwrapping interceptor in
      // api/http.js), so the payload is on .data — same as AllClientsAnalytics.
      const response = await usageService.getClientUsage(params);
      setUsage(response.data);
    } catch (err) {
      setError(err?.response?.data || err?.message || "Unable to load usage data.");
      setUsage(null);
    } finally {
      setLoading(false);
    }
  }, [effectiveClientId, months]);

  useEffect(() => {
    // Don't fire a request we know has no client to scope to — it would 400 and show a
    // developer-ish error where a plain "pick a client" prompt belongs.
    if (allowed && effectiveClientId) load();
  }, [allowed, effectiveClientId, load]);

  const exportRows = useMemo(
    () =>
      (usage?.series || []).map((point) => ({
        Period: point.period,
        Month: point.label,
        "Usage Units": point.usageUnits,
        "Percent Of Limit": point.percentOfLimit ?? "",
        "In Progress": point.isPartial ? "Yes" : "No",
      })),
    [usage]
  );

  if (!allowed) {
    return (
      <Alert variant="danger">
        You do not have permission to view usage analytics.
      </Alert>
    );
  }

  // Internal users start with no client selected; the left menu disables the other
  // client-scoped pages in the same situation.
  if (!effectiveClientId) {
    return (
      <div>
        {!embedded && <Title text="My Usage" icon={<FaChartLine className="mb-1" />} />}
        <Alert variant="info">
          Select a client from the menu at the top of the page to see their usage.
        </Alert>
      </div>
    );
  }

  const current = usage?.currentMonth;
  // The two complete months behind the MoM and Trend figures, e.g. "Jun 2026 → Jul 2026".
  const momPeriod = monthOverMonthPeriod(usage?.series);

  // Four cards at md={3}, matching the Azure Costs page. "Percent of limit" is not a
  // card because the tier band above already shows it as a progress bar; Change and
  // Trend are one card because the arrow IS the direction.
  const insightCards = usage
    ? [
        {
          label: `${current?.label || "This month"} so far`,
          value: formatUnits(current?.usageUnits),
          icon: <FaCalendarAlt size={14} />,
          caption: `${current?.daysElapsed} of ${current?.daysInMonth} days elapsed`,
        },
        {
          label: "Projected month end",
          value: formatUnits(current?.projectedUnits),
          icon: <FaBullseye size={14} />,
          caption:
            current?.projectedPercentOfLimit !== null &&
            current?.projectedPercentOfLimit !== undefined
              ? `${formatPercentOfLimit(current.projectedPercentOfLimit)} of limit`
              : "no tier limit set",
        },
        {
          label: "3-month average",
          value: formatUnits(usage.trailingThreeMonthAverage),
          icon: <FaChartBar size={14} />,
          caption: "completed months only",
        },
        {
          // Named for the months it actually compares. This is NOT "vs the month you are
          // looking at" — the in-progress month is excluded from the calculation.
          label: "Change vs prior month",
          value: formatChange(usage.monthOverMonthPercent),
          icon: <FaChartLine size={14} />,
          trend: usage.monthOverMonthPercent,
          caption: momPeriod || "not enough complete months",
        },
      ]
    : [];

  return (
    <>
      {/* Header row with the view toggle on the right, matching Azure Costs. */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        {!embedded ? (
          <Title text="My Usage" icon={<FaChartLine className="mb-1" />} />
        ) : (
          <div />
        )}
        <ButtonGroup>
          <Button
            variant={view === "chart" ? "primary" : "outline-primary"}
            size="sm"
            onClick={() => setView("chart")}
          >
            <FaChartLine className="me-1" /> Chart
          </Button>
          <Button
            variant={view === "table" ? "primary" : "outline-primary"}
            size="sm"
            onClick={() => setView("table")}
          >
            <FaTable className="me-1" /> Table
          </Button>
        </ButtonGroup>
      </div>

      {error && <Alert variant="danger">{String(error)}</Alert>}

      <UsageInsights cards={insightCards} loading={loading} />

      {/* Filter card, matching the Azure Costs filter bar. */}
      <Card className="mb-3">
        <Card.Body>
          <Row className="align-items-end g-2">
            <Col md={3}>
              <Form.Group controlId="clientUsagePeriod">
                <Form.Label>Period</Form.Label>
                <Form.Select
                  size="sm"
                  value={months}
                  onChange={(e) => setMonths(Number(e.target.value))}
                >
                  {MONTH_RANGE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={5}>
              {usage && (
                <div className="small">
                  <span className="fw-semibold">{usage.clientName}</span>{" "}
                  <span className="text-muted">({usage.clientId})</span>
                </div>
              )}
            </Col>
            <Col md={4} className="text-md-end">
              {exportRows.length > 0 && (
                <ExportToExcelButton
                  data={exportRows}
                  filename={`usage-${usage?.clientId || "client"}`}
                />
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading && (
        <Card className="mb-3">
          <Card.Body>
            <Placeholder as="div" animation="glow">
              <Placeholder xs={4} /> <Placeholder xs={6} />
              <Placeholder xs={12} style={{ height: "200px" }} />
            </Placeholder>
          </Card.Body>
        </Card>
      )}

      {!loading && usage && (
        <>
          <TierProgress usage={usage} />

          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <span>Monthly usage units</span>
              {usage.forecast?.length > 0 && (
                <span className="small text-muted">
                  Forecast confidence{" "}
                  <Badge bg={getConfidenceVariant(usage.forecasting?.confidence)}>
                    {usage.forecasting?.confidence}
                  </Badge>{" "}
                  · {usage.forecasting?.method === "LinearRegression" ? "linear fit" : "carry forward"} over{" "}
                  {usage.forecasting?.monthsUsed} complete month
                  {usage.forecasting?.monthsUsed === 1 ? "" : "s"}
                </span>
              )}
            </Card.Header>
            <Card.Body>
              {view === "chart" ? (
                <>
                  <UsageTrendChart
                    series={usage.series}
                    forecast={usage.forecast}
                    effectiveLimit={usage.effectiveLimit}
                    projectedCurrent={current?.projectedUnits}
                  />
                  <div className="text-muted small mt-2">
                    The current month is shaded lighter because it is still in progress — its total
                    is not comparable to a completed month.
                  </div>
                </>
              ) : (
                <div className="table-responsive">
                  <Table striped hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th className="text-end">Usage units</th>
                        <th className="text-end">Percent of limit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...usage.series, ...usage.forecast].map((point) => (
                        <tr key={`${point.period}-${point.isForecast ? "f" : "a"}`}>
                          <td>
                            {pointLabel(point)}
                            {point.isPartial && (
                              <Badge bg="warning" text="dark" className="ms-2">
                                In progress
                              </Badge>
                            )}
                          </td>
                          <td className="text-end">{formatUnitsExact(point.usageUnits)}</td>
                          <td className="text-end">{formatPercentOfLimit(point.percentOfLimit)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
            {usage.lastUpdated && (
              <Card.Footer className="text-muted small">
                Usage last refreshed {new Date(usage.lastUpdated).toLocaleString()}
              </Card.Footer>
            )}
          </Card>

          <div className="mt-3 small text-muted">
            {momPeriod ? (
              <>
                The change figure compares <strong>{momPeriod}</strong> — the two most recent completed
                months. {current?.label} is still in progress, so it is reported separately as
                &ldquo;this month so far&rdquo; and its projection, and is excluded from these figures.
              </>
            ) : (
              <>
                {current?.label} is still in progress, so it is reported separately from the completed
                months rather than compared against them.
              </>
            )}
          </div>
        </>
      )}
    </>
  );
}

export default UsageAnalytics;
